// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.
/* istanbul ignore file */
import { groupBy } from '@shared/data-access/utils/groupby';
import { type WatchlistResp } from '@shared/data-access/interfaces/api/engine/citec';
import { type WatchListPosition } from '@shared/data-access/interfaces/redux/my-research-state';
import { assetsApi } from '../../api/ai/assets-api';
import { type AssetsInfoResp } from '@shared/data-access/interfaces/api/ai/assets';

import { type ThunkDispatch } from '@reduxjs/toolkit';

export const buildWatchlistPositions = async (
  data: WatchlistResp[],
  dispatch: ThunkDispatch<any, any, any>,

  currency: string,
): Promise<WatchListPosition[]> => {
  const assetTypes = Object.fromEntries(groupBy(data, 'asset_type').entries());

  const requests = Object.keys(assetTypes).map(assetType =>
    dispatch(
      assetsApi.endpoints.assetsInfo.initiate(
        {
          params: { detail: true, currency },
          payload: assetTypes[assetType].map(({ ticker }) => ticker),
          assetType,
        },
        { forceRefetch: true },
      ),
    ).unwrap(),
  );

  const assetsInfo = await Promise.all(requests);

  const assetsList = assetsInfo.flat();

  const assetsDiff = data.filter(
    e => !assetsList.find(({ ticker }) => ticker === e.ticker),
  ) as AssetsInfoResp[];

  assetsList.push(...assetsDiff);

  const watchlist = assetsList.map((item: any) => {
    const watchListItem = data.find(({ ticker }) => ticker === item.ticker);
    return {
      pb: item?.pb,
      pe: item?.pe,
      market_cap: item?.market_cap,
      yield: item?.dividend_yield,
      url_logo: item?.url_logo,
      url_morningstar: item?.url_morningstar,
      ytd_return: item.ytd_return,
      y1_return: item.y1_return,
      y3_return: item.y3_return,
      y5_return: item.y5_return,
      y10_return: item.y10_return,
      y1_vol: item.y1_vol,
      y3_vol: item.y3_vol,
      y5_vol: item.y5_vol,
      y10_vol: item.y10_vol,
      max_draw_down: item.max_draw_down,
      currency: item?.currency,
      last_update: item?.last_update,
      inception_return: item?.inception_return,
      m1_return: item?.m1_return,
      m3_return: item?.m3_return,
      m6_return: item?.m6_return,
      price: item?.price,
      price_as_of: item?.price_as_of,
      asset_class:
        watchListItem?.asset_type === 'stock' ? 'Equity' : item.asset_class,
      ...watchListItem,
    } as WatchListPosition;
  });

  return watchlist as WatchListPosition[];
};
